/**
 * ManageTeamModal component - Modal for managing project team members
 * 
 * Provides interface to:
 * - View current team members and their roles
 * - Assign new team members with Editor or Viewer roles
 * - Edit existing role assignments
 * - Remove team members from the project
 */

import React, { useState } from 'react';
import { X, UserPlus } from 'lucide-react';
import Button from './Button';
import ProjectRoleForm from './ProjectRoleForm';
import { Project } from '../types/project';
import { ProjectRole } from '../types/role';
import { User } from '../types/user';

interface ManageTeamModalProps {
  project: Project;
  projectRoles: ProjectRole[];
  users: User[];
  onClose: () => void;
  onRoleSuccess: () => void;
}

const ManageTeamModal: React.FC<ManageTeamModalProps> = ({
  project,
  projectRoles,
  users,
  onClose,
  onRoleSuccess,
}) => {
  const [showRoleForm, setShowRoleForm] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<ProjectRole | undefined>(undefined);

  const projectRolesList = projectRoles.filter((r) => r.project_id === project.id);

  const getUserName = (userId: string): string => {
    const user = users.find((u) => u.id === userId);
    return user ? user.full_name || user.username : 'Unknown User';
  };

  const handleRoleSuccess = () => {
    setShowRoleForm(false);
    setEditingRole(undefined);
    onRoleSuccess();
  };

  const handleRoleCancel = () => {
    setShowRoleForm(false);
    setEditingRole(undefined);
  };

  const handleEditRole = (role: ProjectRole) => {
    setEditingRole(role);
    setShowRoleForm(true);
  };

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                Manage Team
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {project.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Add Team Member Button */}
            <div className="mb-6">
              <Button
                onClick={() => {
                  setEditingRole(undefined);
                  setShowRoleForm(!showRoleForm);
                }}
                variant={showRoleForm ? 'outline' : 'primary'}
                size="md"
                leftIcon={showRoleForm ? X : UserPlus}
              >
                {showRoleForm ? 'Cancel' : 'Assign Team Member'}
              </Button>
            </div>

            {/* Role Assignment Form */}
            {showRoleForm && (
              <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {editingRole ? 'Edit Role Assignment' : 'Assign New Team Member'}
                </h3>
                <ProjectRoleForm
                  projectId={project.id}
                  role={editingRole}
                  onSuccess={handleRoleSuccess}
                  onCancel={handleRoleCancel}
                />
              </div>
            )}

            {/* Team Members List */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Current Team Members ({projectRolesList.length})
              </h3>

              {projectRolesList.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/50 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                  <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">
                    No team members assigned yet
                  </p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm">
                    Click "Assign Team Member" above to add team members to this project
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {projectRolesList.map((role) => (
                    <div
                      key={role.id}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            {/* User Avatar */}
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-semibold">
                              {getUserName(role.user_id).charAt(0).toUpperCase()}
                            </div>
                            
                            {/* User Info */}
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {getUserName(role.user_id)}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span
                                  className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-md ${
                                    role.role === 'Editor'
                                      ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                                  }`}
                                >
                                  {role.role}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  Added {new Date(role.assigned_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Edit Button */}
                        <Button
                          onClick={() => handleEditRole(role)}
                          variant="outline"
                          size="sm"
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <Button onClick={onClose} variant="primary" size="md">
              Done
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageTeamModal;
